// src/pages/TherapistPage/CaseHistory/WordAnnotation.js
//
// The word list, one word at a time.
//
// This is where PCC becomes trustworthy. The figure computed from speech
// recognition alone is a guess about a child's consonants; the figure computed
// from what a therapist actually heard is a measurement. The screen keeps the
// two apart and this is how a word moves from one to the other.
//
// It writes through the existing therapist-review endpoints rather than a new
// set of its own, so a word checked here is checked in the review queue too —
// there is no second review state to drift out of step with the first.
import React, { useCallback, useEffect, useState } from 'react';
import {
    decideWord, fetchAudio, listSessionWords, measuresError, saveWordReview, wordAudioPath,
} from './measuresApi';

const LABELS = [
    { key: 'correct', label: 'Correct' },
    { key: 'incorrect', label: 'Incorrect' },
    { key: 'needs_review', label: 'Unsure' },
];

function WordAudio({ progressId }) {
    const [url, setUrl] = useState(null);
    const [state, setState] = useState('idle');

    useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

    if (state === 'unavailable') return <span className="cf-hint">no audio</span>;
    if (url) return <audio controls src={url} className="cf-audio" />;
    return (
        <button
            type="button" className="btn btn-sm btn-secondary" disabled={state === 'loading'}
            onClick={async () => {
                setState('loading');
                try { setUrl(await fetchAudio(wordAudioPath(progressId))); setState('ready'); }
                catch (err) { setState('unavailable'); }
            }}
        >
            {state === 'loading' ? '…' : '▶'}
        </button>
    );
}

export default function WordAnnotation({ sessionId, onBack }) {
    const [words, setWords] = useState(null);
    const [edits, setEdits] = useState({});
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        setError('');
        return listSessionWords(sessionId)
            .then(({ data }) => { setWords(data); setEdits({}); })
            .catch((err) => setError(measuresError(err, 'Could not load this session’s words.')));
    }, [sessionId]);

    useEffect(() => { load(); }, [load]);

    if (error) return <div className="cf-note red">{error}</div>;
    if (!words) return <div className="cf-empty">Loading…</div>;

    const editOf = (row) => edits[row.id] || {};
    const setEdit = (row, field, value) => setEdits((prev) => ({
        ...prev, [row.id]: { ...(prev[row.id] || {}), [field]: value },
    }));

    const save = async (row, approve) => {
        const edit = editOf(row);
        setBusy(row.id); setError(''); setMessage('');
        try {
            const body = {};
            if (edit.recognized_ipa !== undefined) body.recognized_ipa = edit.recognized_ipa;
            if (edit.therapist_label !== undefined) body.therapist_label = edit.therapist_label;
            if (Object.keys(body).length > 0) await saveWordReview(row.id, body);
            // Approving is what moves this word into the verified PCC. Until then
            // it stays in the provisional figure, which is the honest place for it.
            if (approve) await decideWord(row.id, 'approved');
            await load();
            setMessage(approve ? 'Checked. It now counts towards the verified PCC.' : 'Saved.');
        } catch (err) {
            setError(measuresError(err, 'Could not save that word.'));
        } finally { setBusy(null); }
    };

    const checked = words.filter((w) => w.review_status === 'approved').length;

    return (
        <div>
            <div className="cf-bar">
                <button type="button" className="btn btn-sm btn-secondary" onClick={onBack}>
                    ← Back to measurements
                </button>
                <span className="cf-hint">
                    Session {sessionId} · {checked} of {words.length} words checked
                </span>
            </div>

            {message && <div className="cf-note green">{message}</div>}
            {words.length === 0 && (
                <div className="cf-empty">No word-list recordings in this session yet.</div>
            )}

            {words.map((row) => {
                const edit = editOf(row);
                return (
                    <div key={row.id} className={`cf-word ${row.review_status === 'approved' ? 'checked' : ''}`}>
                        <div className="cf-word-head">
                            <b>{row.target_word || `#${row.id}`}</b>
                            <span className="cf-hint">target {row.expected_ipa || '—'}</span>
                            {row.has_audio ? <WordAudio progressId={row.id} /> : <span className="cf-hint">no audio</span>}
                            {row.review_status === 'approved' && <span className="cf-status locked">checked</span>}
                        </div>

                        <div className="cf-word-body">
                            <label className="cf-field">
                                <span>What the child actually said (IPA)</span>
                                <input
                                    type="text"
                                    value={edit.recognized_ipa ?? (row.recognized_ipa || '')}
                                    onChange={(e) => setEdit(row, 'recognized_ipa', e.target.value)}
                                    placeholder={row.recognized_ipa || 'p aː n iː'}
                                />
                                <span className="cf-hint">
                                    Recognition heard: {row.recognized_text || '—'}
                                    {row.ai_error_type ? ` · called it ${row.ai_error_type}` : ''}
                                </span>
                            </label>

                            <div className="cf-labels">
                                {LABELS.map((option) => (
                                    <button
                                        key={option.key} type="button"
                                        className={`cf-label-btn${(edit.therapist_label ?? row.therapist_label) === option.key ? ' on' : ''}`}
                                        onClick={() => setEdit(row, 'therapist_label', option.key)}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>

                            <div className="cf-actions">
                                <button
                                    type="button" className="btn btn-sm btn-secondary"
                                    disabled={busy === row.id} onClick={() => save(row, false)}
                                >
                                    Save
                                </button>
                                <button
                                    type="button" className="btn btn-sm btn-primary"
                                    disabled={busy === row.id} onClick={() => save(row, true)}
                                >
                                    Save and mark checked
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
